import { TASK_CONTEXT_TYPES, TASK_KINDS, TASK_SIZES, type TaskContextType, type TaskKind, type TaskSize } from '@shared/types/tasks';
import { urlBool, urlList, urlParam, urlText } from '@/lib/url-state/codecs';

/** `#tags` in the URL, without the # and in lower case, as the Board stores them. */
function normalizeTags(tags: readonly string[]): string[] {
  return [...new Set(tags.map((t) => t.trim().replace(/^#/, '').toLowerCase()).filter(Boolean))];
}

/**
 * The Board's URL (url-and-saved-state.mdc). The key names are the ones saved views and links
 * already use, so none had to be renamed. The view is remembered per team (`TASKS_VIEW_ID`), except
 * the search, which starts empty.
 */
export const TASKS_URL = {
  mine: urlBool('mine'),
  following: urlBool('following'),
  assignee: urlList('assignee'),
  label: urlList('label'),
  /** Task types (T-331). Not `type`, which saved views already use for what a task is linked to. */
  taskType: urlList('task_type'),
  size: urlList<TaskSize | 'none'>('size', { values: [...TASK_SIZES, 'none'] }),
  kind: urlList<TaskKind | 'none'>('kind', { values: [...TASK_KINDS, 'none'] }),
  tag: urlParam<string[]>({
    key: 'tag',
    default: () => [],
    parse: (raw) => {
      const tags = normalizeTags(raw.split(','));
      return tags.length ? tags : undefined;
    },
    format: (tags) => normalizeTags(tags).join(','),
    equals: (a, b) => normalizeTags(a).join(',') === normalizeTags(b).join(','),
  }),
  type: urlList<TaskContextType | 'none'>('type', { values: [...TASK_CONTEXT_TYPES, 'none'] }),
  q: urlText('q'),
  archived: urlBool('archived'),
  snoozed: urlBool('snoozed'),
  /**
   * Show each card's AI summary inside it (T-318). How the cards look, not which: no filter. Not
   * remembered (Andrew, 10 Oct): it starts off on each visit; a link or saved view can turn it on.
   */
  summaries: urlBool('summaries', { remember: false }),
};

/** The Board's last view, per team (decision 24 a). */
export const TASKS_VIEW_ID = 'tasks';
